/* KBW slider — shared by our own sections.

   Plain JavaScript, deliberately not an Alpine component. With two sliders on
   one page this theme's Alpine build handed both `x-data` elements the *same*
   object: the second one's init overwrote the first's track and settings while
   the first's autoplay timer kept running, so the hero's timer scrolled the
   collection row underneath it and the hero itself never advanced. A closure
   per element cannot collide, whatever the build does.

   The track is a scroll-snap container, so swiping, trackpads and keyboard
   scrolling already work without any of this. What follows adds the controls
   on top, and stays out of the way when it cannot help: autoplay never starts
   for a visitor who asked for reduced motion, and it pauses while the tab is
   hidden, while the pointer is over the slider and while a control inside it
   has keyboard focus.

   Positions come from the items themselves rather than from the track width,
   so the same code drives a full-bleed hero and a row of four cards.

   Markup contract:
     [data-kbw-slider]  the root, carrying the options below
     [data-kbw-track]   the scroll container; its element children are the items
     [data-kbw-prev]    previous button   (optional)
     [data-kbw-next]    next button       (optional)
     [data-kbw-dot]     one per item, in order (optional)

   Options, as data attributes on the root:
     data-autoplay="true|false"   data-delay="5000"
     data-loop="true|false"       data-pause-on-hover="true|false"
*/

(function () {
  'use strict';

  function setup(root) {
    if (root.dataset.kbwSliderReady === '1') return;

    var track = root.querySelector('[data-kbw-track]');
    if (!track) return;

    var items = track.children;
    var count = items.length;
    if (count === 0) return;

    root.dataset.kbwSliderReady = '1';

    var dots = root.querySelectorAll('[data-kbw-dot]');
    var autoplay = root.dataset.autoplay === 'true';
    var delay = parseInt(root.dataset.delay, 10) || 5000;
    var loop = root.dataset.loop === 'true';
    var pauseOnHover = root.dataset.pauseOnHover === 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) autoplay = false;

    var index = 0;
    var timer = null;
    var settleTimer = null;
    /* Set while the script scrolls the track, so its own smooth scrolling is
       not mistaken for the visitor taking over. */
    var scrolling = false;
    var hovering = false;
    /* Declared out here, not with the drag code below, because settle() has to
       know never to switch snapping back on under a hand that is still moving
       the row. */
    var dragging = false;

    // --- geometry ------------------------------------------------------
    /* Where the track has to be scrolled for item i to sit at its start edge.
       Reading it off the item keeps variable-width cards honest. */
    function offsetOf(i) {
      var item = items[i];
      if (!item) return 0;
      return track.scrollLeft + item.getBoundingClientRect().left - track.getBoundingClientRect().left;
    }

    /* Whichever scroll position the track is closest to right now.

       Answers in positions, never in items. Those are the same thing on the
       hero, where one slide fills the track, but not on a row of seven cards
       showing five: there the far end is position 2, not item 6. Returning the
       item number there broke two things at once — the active dot was one the
       track can never reach, so it is hidden and no dot lit up at all, and
       releasing a drag asked go() for a position past the end, which with
       looping on sent the row back to the first card just as the visitor
       reached the last one. */
    function nearest() {
      var last = positions() - 1;

      var max = track.scrollWidth - track.clientWidth;
      if (max > 0 && track.scrollLeft >= max - 2) return last;

      var current = track.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;
      for (var i = 0; i <= last; i++) {
        var distance = Math.abs(offsetOf(i) - current);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    }

    // --- state ---------------------------------------------------------
    /* How many distinct places the track can stop, and so how many dots are
       worth showing. A row of seven cards showing five has three, not seven:
       the last four dots could never light up, clicking one did nothing, and
       the whole row felt unresponsive.

       Counted from how far the track can actually scroll rather than from how
       many cards "fit". Fitting is a fraction — a phone shows two cards and a
       bit of a third — and rounding it either invented a stop that goes
       nowhere or swallowed a real one. Recomputed on resize, since it changes
       with the breakpoint. */
    function positions() {
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return 1;

      // Every item that can reach the start edge is a stop...
      var stops = 1;
      for (var i = 1; i < count; i++) {
        if (offsetOf(i) > max - 1) break;
        stops++;
      }
      // ...plus the far end itself, where the last item sits flush right.
      return Math.min(stops + 1, count);
    }

    function paint() {
      var limit = positions();

      /* Whatever put it there, an index past the last stop points at a dot
         that is hidden — which showed up as the highlight vanishing entirely
         once the row was dragged to its end. One dot is always lit. */
      if (index > limit - 1) index = limit - 1;
      if (index < 0) index = 0;

      for (var i = 0; i < dots.length; i++) {
        var reachable = i < limit;
        dots[i].hidden = !reachable;
        var on = reachable && i === index;
        dots[i].classList.toggle('is-active', on);
        dots[i].setAttribute('aria-current', on ? 'true' : 'false');
      }

      // Nothing to scroll, nothing to steer: one lone dot is just clutter.
      if (dots.length && dots[0].parentElement) {
        dots[0].parentElement.hidden = limit <= 1;
      }
    }

    /* End of a glide: stop ignoring scroll events, and let snapping back in.
       Clears both ways of being told the glide is over, so neither can fire
       later against a scroll it knows nothing about. */
    function settle() {
      window.clearTimeout(settleTimer);
      track.removeEventListener('scrollend', onScrollEnd);
      scrolling = false;
      if (!dragging) track.style.scrollSnapType = '';
    }

    function onScrollEnd() {
      settle();
    }

    /* `wrap` defaults to the section's loop setting. Dragging passes false:
       an arrow asking for one past the end is a request to start over, but a
       hand pushing the row is pushing against its end, and wrapping there
       yanked the row back to the first card exactly as the visitor arrived at
       the last one. Nothing else scrolls that way, so nor does this. */
    function go(i, wrap) {
      var last = positions() - 1;
      if (wrap === undefined) wrap = loop;
      if (i < 0) i = wrap ? last : 0;
      if (i > last) i = wrap ? 0 : last;

      index = i;
      paint();

      scrolling = true;
      track.scrollTo({ left: offsetOf(i), behavior: 'smooth' });

      /* Wait for the glide to land before letting go of two things: the flag
         that tells the scroll handler to keep its hands off, and scroll
         snapping.

         Snapping in particular must not come back mid-flight. Setting
         scroll-snap-type to mandatory while the track sits between two items
         makes the browser jump to the nearest one instantly, which is what
         turned every drag into an abrupt landing.

         `scrollend` is the precise signal; the timer is the fallback for
         browsers without it, and the backstop if the event never arrives.

         The listener is a plain one that settle() takes back off, not a
         once-only one that waits to be used up. Asking to scroll somewhere the
         track already is — a small drag that lands back on the position it
         started from, or any drag once the row is against its end — moves
         nothing, so no scrollend ever comes and a once-only listener would sit
         there armed. The next drag then fired it mid-pull and handed snapping
         back while the row was still moving, which is precisely the jerk this
         setTimeout dance exists to avoid: fluid until you let go somewhere,
         stiff from then on. */
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, 900);
      if ('onscrollend' in window) {
        track.addEventListener('scrollend', onScrollEnd);
      }
    }

    function next() { go(index + 1); }
    function prev() { go(index - 1); }

    // --- autoplay ------------------------------------------------------
    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (!autoplay || count < 2 || hovering) return;
      stop();
      timer = window.setInterval(next, delay);
    }

    if (pauseOnHover) {
      root.addEventListener('mouseenter', function () { hovering = true; stop(); });
      root.addEventListener('mouseleave', function () { hovering = false; start(); });
    }
    /* Keyboard equivalent of hover: do not move an item out from under someone
       tabbing through the controls on it. */
    root.addEventListener('focusin', function () { hovering = true; stop(); });
    root.addEventListener('focusout', function () { hovering = false; start(); });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    // --- controls ------------------------------------------------------
    var prevButton = root.querySelector('[data-kbw-prev]');
    var nextButton = root.querySelector('[data-kbw-next]');
    if (prevButton) prevButton.addEventListener('click', prev);
    if (nextButton) nextButton.addEventListener('click', next);

    for (var d = 0; d < dots.length; d++) {
      (function (i) {
        dots[i].addEventListener('click', function () { go(i); });
      })(d);
    }

    /* Keeps the dots honest when the visitor scrolls instead of using them. */
    track.addEventListener(
      'scroll',
      function () {
        if (scrolling) return;
        var i = nearest();
        if (i !== index) {
          index = i;
          paint();
        }
      },
      { passive: true }
    );

    // --- mouse dragging -------------------------------------------------
    /* Touch and trackpads already scroll the track natively, and that native
       scrolling is better than anything reimplemented here — momentum, rubber
       banding, the lot. A mouse gets nothing, though: pressing and dragging
       inside a scroll container does nothing at all. So only mouse and pen are
       handled, and touch is left alone.

       Deliberately no setPointerCapture. Capturing does keep the drag alive
       when the pointer wanders off the element, but Chrome then retargets the
       compatibility mouse events too — the click lands on the track instead of
       the link under the cursor, and a plain click stops opening the item.
       Listening on the window costs one pair of listeners and leaves the click
       where it belongs. */
    if (count > 1) {
      var startX = 0;
      var startScroll = 0;
      var startIndex = 0;
      var travelled = 0;
      var lastX = 0;
      var lastTime = 0;
      var velocity = 0;

      var onMove = function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        travelled = Math.max(travelled, Math.abs(dx));
        track.scrollLeft = startScroll - dx;

        /* Speed over the last moment of the drag, in pixels per millisecond.
           A quick flick should change item even when it covered little ground —
           waiting for half an item makes the thing feel stuck. */
        var now = event.timeStamp || Date.now();
        var elapsed = now - lastTime;
        if (elapsed > 0) velocity = (event.clientX - lastX) / elapsed;
        lastX = event.clientX;
        lastTime = now;
      };

      var release = function () {
        if (!dragging) return;
        dragging = false;

        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', release);
        window.removeEventListener('pointercancel', release);

        track.classList.remove('is-dragging');

        /* A flick wins over distance; otherwise whichever stop the track is
           closest to. go() glides there and restores snapping once it lands.
           Never wrapping, so a drag stops at the ends the way scrolling does. */
        if (Math.abs(velocity) > 0.45 && travelled > 10) {
          go(velocity < 0 ? startIndex + 1 : startIndex - 1, false);
        } else {
          go(nearest(), false);
        }

        // start() checks hovering itself, so a pointer still resting over the
        // slider keeps autoplay paused.
        start();
      };

      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;

        dragging = true;
        /* Whatever glide was in flight is over — the hand has the row now.
           Its pending settle would otherwise land in the middle of this pull
           and switch snapping back on under it. */
        settle();

        travelled = 0;
        velocity = 0;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = event.timeStamp || Date.now();
        startScroll = track.scrollLeft;
        startIndex = nearest();

        track.style.scrollSnapType = 'none';
        track.classList.add('is-dragging');
        stop();

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', release);
        window.addEventListener('pointercancel', release);
      });

      /* A drag ends in a click on whatever sat under the pointer, which is
         usually a link covering the whole item. Anything past a few pixels was
         a drag, not a click. */
      track.addEventListener(
        'click',
        function (event) {
          if (travelled > 5) {
            event.preventDefault();
            event.stopPropagation();
          }
          travelled = 0;
        },
        true
      );

      // Otherwise the browser starts its own image-drag with a ghost preview.
      track.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (index > positions() - 1) go(positions() - 1);
        else paint();
      }, 150);
    });

    paint();
    start();
  }

  function setupAll() {
    var roots = document.querySelectorAll('[data-kbw-slider]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }

  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', setupAll);
})();
